import { useCallback, useMemo } from 'react';
import { useQuizStore } from '@/stores/quiz-store';
import { trackEvent } from '@/features/analytics';
import type { QuizConfig, QuizStep } from '../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useQuiz(config: QuizConfig) {
  const { currentStepIndex, answers, setStepIndex, setAnswer, complete } = useQuizStore();

  const visibleSteps = useMemo(() => {
    return config.steps.filter((step) => {
      if (!step.showIf) return true;
      return step.showIf(answers);
    });
  }, [config.steps, answers]);

  const currentStep: QuizStep | undefined = visibleSteps[currentStepIndex];
  const totalSteps = visibleSteps.length;

  const countedSteps = useMemo(
    () => visibleSteps.filter((step) => step.countsTowardProgress !== false),
    [visibleSteps],
  );
  const countedTotal = countedSteps.length;
  const showProgressCounter = currentStep ? currentStep.countsTowardProgress !== false : false;
  const countedCurrent = currentStep
    ? countedSteps.findIndex((step) => step.id === currentStep.id) + 1
    : 0;

  const progress =
    countedTotal > 0 && showProgressCounter ? countedCurrent / countedTotal : 0;
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === totalSteps - 1;

  const currentValue = currentStep ? answers[currentStep.id] : undefined;

  const canProceed = useMemo(() => {
    if (!currentStep) return false;
    if (
      currentStep.type === 'info' ||
      currentStep.type === 'processing' ||
      currentStep.type === 'claims' ||
      currentStep.type === 'profile' ||
      currentStep.type === 'signing'
    ) {
      return true;
    }
    if (!currentStep.required) return true;
    if (!currentValue) return false;
    if (Array.isArray(currentValue) && currentValue.length === 0) return false;
    if (typeof currentValue === 'string' && currentValue.trim() === '') return false;
    if (currentStep.type === 'email' && typeof currentValue === 'string') {
      return EMAIL_RE.test(currentValue);
    }
    return true;
  }, [currentStep, currentValue]);

  const goNext = useCallback(() => {
    if (!currentStep) return false;

    // Named funnel event (data-driven). Read answers FRESH from the store: for an
    // auto-advancing choice this runs from a delayed handler whose closure predates
    // the just-made selection, so `answer` / `gender` params would otherwise be stale.
    if (currentStep.event) {
      const freshAnswers = useQuizStore.getState().answers;
      const answer = freshAnswers[currentStep.id];
      trackEvent(currentStep.event, {
        answer: typeof answer === 'string' ? answer : undefined,
        metadata: currentStep.eventParams?.(freshAnswers),
      });
    }

    if (currentStep.nextStepId) {
      const nextId =
        typeof currentStep.nextStepId === 'function'
          ? currentStep.nextStepId(answers)
          : currentStep.nextStepId;

      if (nextId === null) {
        complete();
        return true;
      }

      const idx = visibleSteps.findIndex((s) => s.id === nextId);
      if (idx >= 0) {
        setStepIndex(idx);
        return false;
      }
    }

    if (isLast) {
      complete();
      return true;
    }

    setStepIndex(currentStepIndex + 1);
    return false;
  }, [
    currentStep,
    currentStepIndex,
    isLast,
    answers,
    visibleSteps,
    setStepIndex,
    complete,
  ]);

  const goBack = useCallback(() => {
    // Skip processing screens on back — they auto-advance forward.
    let idx = currentStepIndex - 1;
    while (idx > 0 && visibleSteps[idx]?.type === 'processing') {
      idx -= 1;
    }
    if (idx >= 0) {
      setStepIndex(idx);
    }
  }, [currentStepIndex, setStepIndex, visibleSteps]);

  const setCurrentAnswer = useCallback(
    (value: string | string[]) => {
      if (currentStep) {
        setAnswer(currentStep.id, value);
      }
    },
    [currentStep, setAnswer],
  );

  return {
    currentStep,
    currentStepIndex,
    currentValue,
    totalSteps,
    countedCurrent,
    countedTotal,
    showProgressCounter,
    progress,
    isFirst,
    isLast,
    canProceed,
    goNext,
    goBack,
    setCurrentAnswer,
  };
}
